import { CircularProgress, Typography, Box, Chip, Stack, TextField, Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Grid } from "@mui/material";
import axios from "axios";
import { useState } from "react";
import AdminNavigation from "Navigation/AdminNavigation";
import Layout from "../../../Components/Layout/Layout";
import useAxiosEffect from "../../../system/useAxiosEffect";
import JobsHubSidebar from "./JobsHubSidebar";
import Search from "../../../Components/Search";
import { useHistory } from "react-router-dom";
import ListItem from "../../../Components/ListItem";
import ListItemContent from "../../../Components/ListItemContent";
import ListItemActions from "../../../Components/ListItemActions";
import ListItemAction from "../../../Components/ListItemAction";
import Check from "@mui/icons-material/Check";
import Clear from "@mui/icons-material/Clear";
import SendIcon from "@mui/icons-material/Send";
import Launch from "@mui/icons-material/Launch";
import { ThemeColor } from "../../../system/Domain";

export interface JobMetadata {
	name: string;
	jobType?: string;
	route: string;
	description?: string;
	parameters: JobParameter[];
}

export interface JobParameter {
	name: string;
	type: string;
	isNullable: boolean;
	defaultValue?: any;
	hint?: string;
	isRequired?: boolean;
	example?: string;
}

const JobsHub = () => {
	type Mode = "view" | "action" | "result";

	const [jobs, setJobs] = useState<JobMetadata[]>();
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [modes, setModes] = useState<Record<string, Mode>>({});
	const [invokedJobId, setInvokedJobId] = useState<string | null>(null);
	const [formValues, setFormValues] = useState<Record<string, any>>({});
	const [loading, setLoading] = useState<Record<string, boolean>>({});
	const [error, setError] = useState<string | null>(null);
	const [results, setResults] = useState<Record<string, any>>({});
	const history = useHistory();

	const getMode = (jobRoute: string): Mode => modes[jobRoute] || "view";
	const setMode = (jobRoute: string, mode: Mode) => {
		setModes(prev => ({ ...prev, [jobRoute]: mode }));
	};

	useAxiosEffect(
		async (config) => {
			const { data } = await axios.get<JobMetadata[]>(`/api/admin/jobs/metadata`, config);
			setJobs(data);
		},
		[]
	);

	const filteredJobs = jobs?.filter(j => 
		(j.jobType ?? j.name).toLowerCase().includes(searchTerm.toLowerCase()) ||
		j.route.toLowerCase().includes(searchTerm.toLowerCase()) ||
		j.parameters.some(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()))
	) || [];

	const getParameterTypeColor = (type: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
		switch (type) {
			case "string": return "primary";
			case "number": return "info";
			case "boolean": return "success";
			case "List": return "secondary";
			default: return "default";
		}
	};

	const getModeColor = (mode: Mode): ThemeColor => {
		switch (mode) {
			case "action": return "primary";
			case "result": return "success";
			default: return "info";
		}
	};

	const handleParameterChange = (jobRoute: string, paramName: string, value: any) => {
		const key = `${jobRoute}`;
		setFormValues(prev => ({
			...prev,
			[key]: {
				...prev[key],
				[paramName]: value === "" ? undefined : value
			}
		}));
	};

	const handleInvokeJob = async (job: JobMetadata) => {
		setLoading(prev => ({ ...prev, [job.route]: true }));
		setError(null);

		try {
			const key = job.route;
			const params = formValues[key] || {};
			
			const response = await axios.post(`/api/${job.route}`, params);
			const result = response.data;
			
			if (result) {
				setResults(prev => ({ ...prev, [key]: result }));
				setMode(job.route, "result");
				setInvokedJobId(null);
				setFormValues(prev => {
					const updated = { ...prev };
					delete updated[key];
					return updated;
				});
			}
		} catch (err: any) {
			setError(err.response?.data?.message || err.message || "Failed to invoke job");
		} finally {
			setLoading(prev => ({ ...prev, [job.route]: false }));
		}
	};

	return (
		<Layout
			title="Jobs Hub"
			navigation={<AdminNavigation />}
			// sidebar={<JobsHubSidebar />}
		>
			<Box mb={2}>
				<Search
					value={searchTerm}
					placeholder="Search jobs or parameters..."
					realtime
					onSearch={(value) => setSearchTerm(value)}
				/>
			</Box>

			{invokedJobId && (
				<Alert 
					severity="success" 
					sx={{ mb: 2 }}
					onClose={() => {
						setInvokedJobId(null);
						history.push(`/admin/jobs/${invokedJobId}`);
					}}
				>
					Job invoked successfully! ID: <strong>{invokedJobId}</strong>. Click here to view the job or close this message.
				</Alert>
			)}

			{error && (
				<Alert 
					severity="error" 
					sx={{ mb: 2 }}
					onClose={() => setError(null)}
				>
					{error}
				</Alert>
			)}

			{!jobs && <CircularProgress size={48} />}
			{jobs && (
				<Stack spacing={2}>
					<Typography variant="body2" color="textSecondary">
						Found {filteredJobs.length} job{filteredJobs.length !== 1 ? "s" : ""}
					</Typography>
					{filteredJobs.map((job) => {
						const mode = getMode(job.route);
						const formData = formValues[job.route] || {};
						const isLoading = loading[job.route] || false;
						const color = getModeColor(mode);
						const result = results[job.route];
						const resultIsString = typeof result === "string";
						const resultJobId = resultIsString ? result : result?.id;

						return (
							<ListItem key={job.route} color={color}>

								<ListItemActions color={color} disabled={isLoading}>
									{mode === "view" && (
										<ListItemAction
											icon={<SendIcon />}
											tooltip="Invoke job"
											onClick={() => setMode(job.route, "action")}
										/>
									)}
									{mode === "action" && (
										<>
											<ListItemAction
												icon={<Check />}
												tooltip="Invoke job"
												disabled={isLoading}
												onClick={() => handleInvokeJob(job)}
											/>
											<ListItemAction
												icon={<Clear />}
												tooltip="Cancel"
												disabled={isLoading}
												onClick={() => setMode(job.route, "view")}
											/>
										</>
									)}
									{mode === "result" && (
										<>
											{resultJobId && (
												<ListItemAction
													icon={<Launch />}
													tooltip="Go to job"
													onClick={() => history.push(`/admin/jobs/${resultJobId}`)}
												/>
											)}
											<ListItemAction
												icon={<Clear />}
												tooltip="Close"
												onClick={() => setMode(job.route, "view")}
											/>
										</>
									)}
								</ListItemActions>
								<ListItemContent color={color}>
									<Typography variant="h6" gutterBottom>
										{job.jobType || job.name}
									</Typography>
									<Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: "block" }}>
										Route: <code>{job.route}</code>
									</Typography>
									{job.description && (
										<Typography variant="body2" sx={{ mb: 2, fontStyle: "italic", color: "text.secondary" }}>
											{job.description}
										</Typography>
									)}

									{mode === "view" && (
										<>
											{job.parameters.length === 0 ? (
												<Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
													No parameters
												</Typography>
											) : (
												<Box sx={{ mt: 2 }}>
													<TableContainer>
														<Table size="small">
															<TableHead>
																<TableRow>
																	<TableCell><strong>Parameter</strong></TableCell>
																	<TableCell><strong>Type</strong></TableCell>
																	<TableCell><strong>Required</strong></TableCell>
																	<TableCell><strong>Nullable</strong></TableCell>
																	<TableCell><strong>Default</strong></TableCell>
																	<TableCell><strong>Hint</strong></TableCell>
																</TableRow>
															</TableHead>
															<TableBody>
																{job.parameters.map((param) => (
																	<TableRow key={param.name}>
																		<TableCell>
																			<Typography variant="body2">
																				{param.name}
																			</Typography>
																		</TableCell>
																		<TableCell>
																			<Chip 
																				label={param.type} 
																				size="small" 
																				color={getParameterTypeColor(param.type)}
																				variant="outlined"
																			/>
																		</TableCell>
																		<TableCell>
																			{param.isRequired ? (
																				<Chip label="Yes" size="small" color="error" variant="filled" />
																			) : (
																				<Chip label="No" size="small" variant="outlined" />
																			)}
																		</TableCell>
																		<TableCell>
																			{param.isNullable ? (
																				<Chip label="Yes" size="small" variant="outlined" />
																			) : (
																				<Chip label="No" size="small" variant="outlined" />
																			)}
																		</TableCell>
																		<TableCell>
																			{param.defaultValue !== null && param.defaultValue !== undefined ? (
																				<code>{JSON.stringify(param.defaultValue)}</code>
																			) : (
																				<Typography variant="caption" color="textSecondary">—</Typography>
																			)}
																		</TableCell>
																		<TableCell>
																			<Typography variant="caption">
																				{param.hint || (param.example && `Example: ${param.example}`)}
																			</Typography>
																		</TableCell>
																	</TableRow>
																))}
															</TableBody>
														</Table>
													</TableContainer>
												</Box>
											)}
										</>
									)}

									{mode === "action" && (
										<Box sx={{ mt: 2 }}>
											{job.parameters.length === 0 ? (
												<Typography variant="body2" color="textSecondary">
													No parameters required
												</Typography>
											) : (
												<Grid container spacing={2}>
													{job.parameters.map((param) => (
														<Grid item xs={12} key={param.name}>
															<TextField
																fullWidth
																label={param.name}
																placeholder={
																	param.isRequired 
																		? "Required" 
																		: param.defaultValue !== null && param.defaultValue !== undefined
																		? `Default: ${JSON.stringify(param.defaultValue)}`
																		: "Leave empty"
																}
																value={formData[param.name] ?? ""}
																onChange={(e) => handleParameterChange(job.route, param.name, e.target.value)}
																size="small"
																type={param.type === "number" ? "number" : "text"}
																helperText={
																	param.hint || 
																	(param.isRequired ? "Required" : "") ||
																	(param.example ? `Example: ${param.example}` : "")
																}
																disabled={isLoading}
															/>
														</Grid>
													))}
												</Grid>
											)}
										</Box>
									)}

									{mode === "result" && (
										<Box sx={{ mt: 2 }}>
											<Typography variant="subtitle2" sx={{ mb: 1 }}>
												Result:
											</Typography>
											{resultIsString ? (
												<Typography variant="body2" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
													{result}
												</Typography>
											) : (
												<Box sx={{ p: 1, bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 1, maxHeight: 300, overflow: "auto" }}>
													<code style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: "0.85rem" }}>
														{JSON.stringify(result, null, 2)}
													</code>
												</Box>
											)}
										</Box>
									)}
								</ListItemContent>
							</ListItem>
						);
					})}
				</Stack>
			)}
		</Layout>
	);
};

export default JobsHub;
